import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../components/LanguageSelector/translations';

const ProductPaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);

  const t = translations[language] || translations.en;
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Simulate loading time for better UX
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t.payment.processing || 'Processing your payment...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t.productPayment.successTitle || 'Payment Successful!'}
          </h1>
          
          <p className="text-lg text-gray-600 mb-6">
            {t.productPayment.successMessage || 'Thank you for your order. Your payment has been processed successfully.'}
          </p>

          {sessionId && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                <strong>{(t.payment as any).sessionId || 'Session ID'}:</strong> {sessionId}
              </p>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-blue-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">
              {t.productPayment.nextSteps || 'What happens next?'}
            </h3>
            <ul className="text-left text-blue-800 space-y-2">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                {t.productPayment.emailConfirmation || 'You will receive a confirmation email shortly'}
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                {t.productPayment.orderProcessing || 'We will process your order and prepare it for shipping'}
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                {t.productPayment.shippingUpdate || 'You will receive shipping updates via email'}
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/account')}
              className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors font-medium"
            >
              {t.productPayment.viewOrders || 'View My Orders'}
            </button>
            
            <button
              onClick={() => navigate('/products')}
              className="bg-gray-600 text-white px-6 py-3 rounded-md hover:bg-gray-700 transition-colors font-medium"
            >
              {t.productPayment.continueShopping || 'Continue Shopping'}
            </button>
          </div>

          {/* Contact Information */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-2">
              {t.productPayment.questions || 'Have questions about your order?'}
            </p>
            <p className="text-sm text-gray-600">
              {t.productPayment.contactUs || 'Contact us at'} {' '}
              <a 
                href="mailto:info@ladolcevitareis.nl" 
                className="text-green-600 hover:text-green-700 font-medium"
              >
                info@ladolcevitareis.nl
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPaymentSuccessPage;
